import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import {
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  Modal,
  FormRow,
  Divider,
  Check,
  ArrowLeft,
  ArrowRight,
} from "@/components/ds";

// -- Step definitions --------------------------------------------------------

type Step = "choice" | "confirmStartOver" | "connect" | "configure" | "review" | "progress" | "done";
const WIZARD_STEPS: Step[] = ["connect", "configure", "review", "progress", "done"];

const STEP_LABELS: Record<Step, string> = {
  choice: "",
  confirmStartOver: "",
  connect: "Connect",
  configure: "Configure",
  review: "Review",
  progress: "Progress",
  done: "Done",
};

interface ZendeskImportConfigDto {
  id: number;
  subdomain: string;
  email: string;
  status: string;
  connectedAt: string | null;
  lastVerifiedAt: string | null;
}

interface ImportJobDto {
  id: number;
  status: "Pending" | "Running" | "Paused" | "Complete" | "Failed";
  importedCount: number;
  skippedCount: number;
  errorCount: number;
  totalEstimate: number | null;
  lastRunAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

function StepBar({ current }: { current: Step }) {
  const idx = WIZARD_STEPS.indexOf(current);
  return (
    <div className="flex items-center gap-0 mb-8">
      {WIZARD_STEPS.map((s, i) => {
        const done = i < idx;
        const active = s === current;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                done ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-white"
                     : active ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-white"
                              : "border-zinc-300 text-zinc-400 bg-white"
              }`}>
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${active ? "text-[var(--color-primary)]" : done ? "text-zinc-600" : "text-zinc-400"}`}>
                {STEP_LABELS[s]}
              </span>
            </div>
            {i < WIZARD_STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 mx-1 ${i < idx ? "bg-[var(--color-primary)]" : "bg-zinc-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

interface Props {
  onClose(): void;
}

export default function ZendeskImportWizard({ onClose }: Props) {
  const api = useApi();
  const [step, setStep] = useState<Step>("choice");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [existingConfig, setExistingConfig] = useState<ZendeskImportConfigDto | null>(null);
  const [existingJob, setExistingJob] = useState<ImportJobDto | null>(null);
  const [configId, setConfigId] = useState<number | null>(null);
  // Set only via the explicit confirmStartOver step - threaded through to the final
  // POST Configs/{id}/import call so "Start Over" actually resets progress server-side
  // instead of silently resuming the prior job (Opus review finding, 2026-07-25).
  const [startOverConfirmed, setStartOverConfirmed] = useState(false);

  // -- Connect step ----------------------------------------------------------
  const [subdomain, setSubdomain] = useState("");
  const [email, setEmail] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [verified, setVerified] = useState(false);
  const [testing, setTesting] = useState(false);

  // -- Configure step ---------------------------------------------------------
  const [allTime, setAllTime] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "open_and_closed" | "closed_only">("all");

  // -- Progress step -----------------------------------------------------------
  const [job, setJob] = useState<ImportJobDto | null>(null);

  // -- Load on open, re-entrancy detection ------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.admin.get("ZendeskImport/Configs");
        const data = await resp.json();
        const configs: ZendeskImportConfigDto[] = Array.isArray(data) ? data : [];
        if (configs.length > 0) {
          const cfg = configs[0];
          setExistingConfig(cfg);
          setConfigId(cfg.id);
          setSubdomain(cfg.subdomain);
          setEmail(cfg.email);
          setVerified(cfg.status === "verified");

          const statusResp = await api.admin.get(`ZendeskImport/Configs/${cfg.id}/import/status`);
          const statusData = await statusResp.json();
          if (statusData && statusData.status !== "not_started") {
            setExistingJob(statusData);
          }
          setStep("choice");
        } else {
          setStep("connect");
        }
      } catch (err) {
        setError(`Failed to load existing Zendesk import setup: ${err instanceof Response ? (err as Response).status : String(err)}`);
        setStep("connect");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- Actions -----------------------------------------------------------------

  async function handleTestConnection() {
    setTesting(true);
    setError("");
    try {
      let id = configId;
      if (!id) {
        const createResp = await api.admin.post("ZendeskImport/Configs", { subdomain, email, apiToken });
        if (!createResp.ok) throw createResp;
        const created = await createResp.json();
        id = created.id;
        setConfigId(id);
      } else if (apiToken) {
        const updateResp = await api.admin.put(`ZendeskImport/Configs/${id}`, { subdomain, email, apiToken });
        if (!updateResp.ok) throw updateResp;
      }

      const testResp = await api.admin.post(`ZendeskImport/Configs/${id}/test`, {});
      const testData = await testResp.json();
      if (!testResp.ok || !testData.verified) {
        setVerified(false);
        setError(testData.error || "Could not verify Zendesk credentials. Check subdomain, email, and API token.");
        return;
      }
      setVerified(true);
      setStep("configure");
    } catch (err) {
      setError(`Failed to verify connection: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setTesting(false);
    }
  }

  async function handleStartImport(startOver: boolean) {
    if (!configId) return;
    setSaving(true);
    setError("");
    try {
      const filterConfigJson = JSON.stringify({
        startTimeUnix: allTime || !startDate ? 0 : Math.floor(new Date(startDate).getTime() / 1000),
        statusFilter,
      });
      const resp = await api.admin.post(`ZendeskImport/Configs/${configId}/import`, { startOver, filterConfigJson });
      if (!resp.ok) throw resp;
      const jobDto = await resp.json();
      setJob(jobDto);
      setStep("progress");
      pollStatus();
    } catch (err) {
      setError(`Failed to start import: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setSaving(false);
    }
  }

  async function pollStatus() {
    if (!configId) return;
    try {
      const resp = await api.admin.get(`ZendeskImport/Configs/${configId}/import/status`);
      const data = await resp.json();
      setJob(data);
      if (data.status === "Running") {
        setTimeout(pollStatus, 4000);
      } else if (data.status === "Complete") {
        setStep("done");
      }
    } catch {
      // Transient poll failure, try again shortly rather than surfacing a hard error mid-import.
      setTimeout(pollStatus, 8000);
    }
  }

  async function handlePause() {
    if (!configId) return;
    try {
      const resp = await api.admin.post(`ZendeskImport/Configs/${configId}/import/pause`, {});
      const data = await resp.json();
      setJob(data);
    } catch (err) {
      setError(`Failed to pause import: ${err instanceof Response ? (err as Response).status : String(err)}`);
    }
  }

  // -- Choice step (re-entrancy per Wizard Design Standard) -------------------

  if (loading) {
    return (
      <Modal open size="md" onClose={onClose}>
        <div className="p-10 text-center text-sm text-zinc-500">Loading...</div>
      </Modal>
    );
  }

  if (step === "choice" && existingConfig) {
    return (
      <Modal open size="md" onClose={onClose}>
        <div className="p-8 space-y-5">
          <h1 className="text-lg font-semibold text-zinc-800">Zendesk Ticket Import</h1>
          <p className="text-sm text-zinc-600">
            You already have a Zendesk connection (<strong>{existingConfig.subdomain}.zendesk.com</strong>).
            {existingJob && (
              <> A previous import is <strong>{existingJob.status.toLowerCase()}</strong>, {existingJob.importedCount} tickets imported so far.</>
            )}
          </p>
          <div className="space-y-2">
            {existingJob && existingJob.status !== "Complete" && (
              <PrimaryButton className="w-full" onClick={() => { setStep("progress"); setJob(existingJob); pollStatus(); }}>
                Continue import
              </PrimaryButton>
            )}
            <SecondaryButton className="w-full" onClick={() => handleStartImport(false)}>
              {existingJob?.status === "Complete" ? "Check for new tickets" : "Extend, pull anything new"}
            </SecondaryButton>
            <GhostButton className="w-full text-red-600" onClick={() => setStep("confirmStartOver")}>
              Start over (reconnect and re-import from scratch)
            </GhostButton>
          </div>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>}
        </div>
      </Modal>
    );
  }

  // Explicit destructive-path confirmation per the Wizard Design Standard - "Start Over" must
  // never proceed without the customer confirming, since it supersedes the existing job (see
  // ZendeskImportController.StartImport's StartOver branch).
  if (step === "confirmStartOver") {
    return (
      <Modal open size="md" onClose={onClose}>
        <div className="p-8 space-y-5">
          <h1 className="text-lg font-semibold text-zinc-800">Start over?</h1>
          <p className="text-sm text-zinc-600">
            This will supersede the current import job{existingJob ? ` (${existingJob.importedCount} tickets imported so far)` : ""} and
            start a brand new import from scratch. The prior job's history is kept for audit purposes but will no longer run.
          </p>
          <div className="flex gap-3">
            <SecondaryButton className="flex-1" onClick={() => setStep("choice")}>Cancel</SecondaryButton>
            <PrimaryButton
              className="flex-1"
              onClick={() => { setStartOverConfirmed(true); setStep("connect"); }}
            >
              Yes, start over
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    );
  }

  const isDone = step === "done";

  return (
    <Modal open size="xl" height="tall" shellClassName="mx-4" onClose={onClose} closeOnEsc={false} closeOnBackdropClick={false}>
      <div className="px-8 pt-8 pb-0">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-lg font-semibold text-zinc-800">Zendesk Ticket Backfill Importer</h1>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 text-xl leading-none" aria-label="Close">x</button>
        </div>
        {!isDone && WIZARD_STEPS.includes(step) && <StepBar current={step} />}
      </div>

      <div className="flex-1 overflow-y-auto px-8 pb-2">
        {step === "connect" && (
          <div className="space-y-4 max-w-lg">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              A Zendesk API token grants full admin-equivalent read access to your entire Zendesk
              account, Zendesk does not offer a read-only token scope. Generate one in{" "}
              <strong>Admin Center -&gt; Apps and integrations -&gt; APIs -&gt; Zendesk API -&gt; Add API token</strong>.
            </div>
            <FormRow label="Zendesk subdomain" hint="e.g. acme for acme.zendesk.com">
              <input className="border rounded-lg px-3 py-2 w-full text-sm" value={subdomain} onChange={e => setSubdomain(e.target.value)} placeholder="acme" />
            </FormRow>
            <FormRow label="Agent email">
              <input className="border rounded-lg px-3 py-2 w-full text-sm" value={email} onChange={e => setEmail(e.target.value)} placeholder="agent@acme.com" />
            </FormRow>
            <FormRow label="API token">
              <input type="password" className="border rounded-lg px-3 py-2 w-full text-sm" value={apiToken} onChange={e => setApiToken(e.target.value)} placeholder={existingConfig ? "Leave blank to keep existing token" : ""} />
            </FormRow>
          </div>
        )}

        {step === "configure" && (
          <div className="space-y-4 max-w-lg">
            <FormRow label="Date range">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={allTime} onChange={() => setAllTime(true)} /> All-time (every ticket ever created)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={!allTime} onChange={() => setAllTime(false)} /> Only tickets created after
                </label>
                {!allTime && (
                  <input type="date" className="border rounded-lg px-3 py-2 text-sm" value={startDate} onChange={e => setStartDate(e.target.value)} />
                )}
              </div>
            </FormRow>
            <FormRow label="Ticket status filter">
              <select className="border rounded-lg px-3 py-2 text-sm w-full" value={statusFilter} onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}>
                <option value="all">All statuses</option>
                <option value="open_and_closed">Open and closed (skip deleted)</option>
                <option value="closed_only">Closed/solved only</option>
              </select>
            </FormRow>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-3 max-w-lg text-sm">
            <div className="bg-zinc-50 rounded-xl p-5 space-y-2">
              <div className="flex justify-between"><span className="text-zinc-500">Zendesk account</span><span className="font-medium">{subdomain}.zendesk.com</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Date range</span><span className="font-medium">{allTime ? "All-time" : `Since ${startDate}`}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Status filter</span><span className="font-medium">{statusFilter.replace("_", " ")}</span></div>
            </div>
            <p className="text-zinc-500">
              Imported tickets never affect your live conversation analytics (CSAT, AI-resolved rate,
              theme reports), they are stored separately and shown read-only on each contact's
              "Imported History" tab.
            </p>
          </div>
        )}

        {step === "progress" && job && (
          <div className="space-y-4 max-w-lg">
            <div className="w-full bg-zinc-100 rounded-full h-3 overflow-hidden">
              <div
                className="bg-[var(--color-primary)] h-3 rounded-full transition-all"
                style={{ width: job.totalEstimate ? `${Math.min(100, (job.importedCount / job.totalEstimate) * 100)}%` : "10%" }}
                role="progressbar"
                aria-valuenow={job.totalEstimate ? Math.round((job.importedCount / job.totalEstimate) * 100) : undefined}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <div className="text-sm text-zinc-600">
              {job.importedCount} tickets imported{job.totalEstimate ? ` of ~${job.totalEstimate}` : ""}
              {job.skippedCount > 0 && <>, {job.skippedCount} skipped</>}
              {job.errorCount > 0 && <>, {job.errorCount} errors</>}
            </div>
            <div className="text-xs text-zinc-400">Status: {job.status}</div>
            {job.status === "Running" && (
              <SecondaryButton onClick={handlePause}>Pause import</SecondaryButton>
            )}
            {job.status === "Paused" && (
              <PrimaryButton onClick={() => handleStartImport(false)}>Resume import</PrimaryButton>
            )}
          </div>
        )}

        {step === "done" && (
          <div className="text-center space-y-4 py-6">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7" />
            </div>
            <h2 className="text-lg font-semibold text-zinc-800">Import complete</h2>
            <p className="text-sm text-zinc-500">
              {job?.importedCount ?? 0} historical tickets imported. View them on each contact's
              "Imported History" tab.
            </p>
            <PrimaryButton onClick={onClose}>Close</PrimaryButton>
          </div>
        )}
      </div>

      {error && (
        <div className="mx-8 mt-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      {!isDone && step !== "progress" && (
        <>
          <Divider className="my-0" />
          <div className="px-8 py-6 flex items-center justify-between">
            <div>
              {step === "connect" ? (
                <GhostButton onClick={onClose}>Cancel</GhostButton>
              ) : (
                <GhostButton onClick={() => setStep(step === "configure" ? "connect" : "configure")}>
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
                </GhostButton>
              )}
            </div>
            <div className="flex items-center gap-3">
              {step === "connect" && (
                <PrimaryButton onClick={handleTestConnection} disabled={testing || !subdomain || !email || (!apiToken && !existingConfig)}>
                  {testing ? "Testing..." : "Test connection"}
                </PrimaryButton>
              )}
              {step === "configure" && (
                <PrimaryButton onClick={() => setStep("review")} disabled={!verified}>
                  Next <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </PrimaryButton>
              )}
              {step === "review" && (
                <PrimaryButton onClick={() => handleStartImport(startOverConfirmed)} disabled={saving}>
                  {saving ? "Starting..." : startOverConfirmed ? "Start over" : "Start import"}
                </PrimaryButton>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
